/**
 * External Roles Tab - partner-safe roles with editable matrix.
 *
 * Mirrors the internal RolesTab UX:
 *  - Expand a role to see template chips + editable permission matrix.
 *  - Template chips are filtered to external-safe groups only.
 *  - Matrix cells for non-external-safe permissions are rendered
 *    disabled (greyed out) with a tooltip - matches what the backend
 *    enforces via the m2m signal on ExternalOrganisationRole.permissions.
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { Loader2, Plus, Trash2, Pencil, ChevronDown } from 'lucide-react'
import { z } from 'zod'
import { PageInfo } from '@/admin/components/page-info'
import { ConfirmDeleteDialog } from '@/admin/components/confirm-delete-dialog'
import { Button } from '@/shared/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/shared/components/ui/dialog'
import api from '@/admin/lib/api'
import { useOrg } from '@/admin/hooks/use-org'
import type { ExternalOrganisationRole } from '@/admin/types/organisations'
import { useForm } from '@/shared/lib/forms'
import type { FormField } from '@/shared/lib/forms'
import { cn } from '@/shared/lib/utils'
import {
  groupsByEntity,
  EXTERNAL_ROLE_FLOOR_CODENAMES,
  GRANT_UNSAFE_CODENAME,
  type Permission,
  type PermissionGroup,
} from '@/admin/lib/permissions'
import { usePermissions } from '@/admin/hooks/use-permissions'
import { TemplateChipRow } from '@/admin/pages/settings/roles-tab'
import { RoleTemplateSuggestions } from '@/admin/pages/settings/role-template-suggestions'
import { PermissionEditorModal } from '@/admin/pages/settings/permission-editor-modal'
import { RoleEditDialog, type RoleEditValues } from '@/admin/pages/settings/role-edit-dialog'

import { toastApiError } from '@/shared/lib/api-error'
type RoleValues = {
  name: string
  description: string
}

// External role shape returned by the API (read-side)
interface ExternalRoleRow extends ExternalOrganisationRole {
  permission_codenames?: string[]
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ExternalRolesTab() {
  const { orgUuid } = useOrg()
  const { t } = useTranslation()
  const { can } = usePermissions()
  // Whether the current admin may deliberately grant a not-recommended
  // (non-floor) permission to a partner role. Gates the reveal-and-grant path in
  // the editor; the backend serializer enforces the same check server-side.
  const canGrantUnsafe = can(GRANT_UNSAFE_CODENAME)
  const [roles, setRoles] = useState<ExternalRoleRow[]>([])
  const [permissionGroups, setPermissionGroups] = useState<PermissionGroup[]>([])
  const [allPermissions, setAllPermissions] = useState<Permission[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  // Create dialog - name + description only. Permissions and templates
  // are configured in the expanded row after the role exists.
  const [dialogOpen, setDialogOpen] = useState(false)
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(
    null,
  )
  const selectedTemplateIdRef = useRef<number | null>(null)
  const selectTemplate = useCallback((id: number | null) => {
    selectedTemplateIdRef.current = id
    setSelectedTemplateId(id)
  }, [])
  const [deletingUuid, setDeletingUuid] = useState<string | null>(null)
  const [confirmRole, setConfirmRole] = useState<ExternalOrganisationRole | null>(
    null,
  )
  const [expandedUuid, setExpandedUuid] = useState<string | null>(null)
  const [permEditorUuid, setPermEditorUuid] = useState<string | null>(null)
  const [savingRoleUuid, setSavingRoleUuid] = useState<string | null>(null)
  // Rename / edit dialog (name + description only).
  const [editRole, setEditRole] = useState<ExternalRoleRow | null>(null)
  const [savingEdit, setSavingEdit] = useState(false)

  const createRoleFields = useMemo<FormField[]>(
    () => [
      {
        type: 'text',
        name: 'name',
        label: 'Role Name',
        placeholder: 'e.g. Dealer, Service Partner',
        value: '',
        validationSchema: z.string().min(1, 'Role name is required'),
      },
      {
        type: 'text',
        name: 'description',
        label: 'Description',
        placeholder: 'Optional description',
        value: '',
      },
    ],
    [],
  )
  const { Form: CreateRoleForm, ref: createRoleFormRef } = useForm(
    {
      name: 'create-external-role-form',
      submit: 'footer',
      fields: createRoleFields,
    },
    async (rawValues) => {
      const values = rawValues as unknown as RoleValues
      setSaving(true)
      try {
        const templateId = selectedTemplateIdRef.current
        const res = await api.post(`/organisations/${orgUuid}/external-roles/`, {
          ...values,
          ...(templateId != null ? { apply_group_ids: [templateId] } : {}),
        })
        toast.success('External role created.')
        selectTemplate(null)
        createRoleFormRef.current?.resetForm()
        setDialogOpen(false)
        await fetchData()
        const created = res.data
        if (created?.uuid) {
          setExpandedUuid(created.uuid)
        }
      } catch (err) {
        toastApiError(err, 'Failed to create role.');
      } finally {
        setSaving(false)
      }
    },
    [dialogOpen, orgUuid, createRoleFields],
  )

  const fetchData = useCallback(async () => {
    if (!orgUuid) {
      setLoading(false)
      return
    }
    try {
      const [rolesRes, groupsRes, permsRes] = await Promise.all([
        api.get(`/organisations/${orgUuid}/external-roles/`),
        api.get('/auth/rbac/permission-groups/'),
        api.get('/auth/rbac/permissions/'),
      ])
      setRoles(rolesRes.data.results ?? rolesRes.data)
      setPermissionGroups(groupsRes.data.results ?? groupsRes.data)
      setAllPermissions(permsRes.data.results ?? permsRes.data)
    } catch (err) {
      toastApiError(err, 'Failed to load external roles.');
    } finally {
      setLoading(false)
    }
  }, [orgUuid])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  // Derived
  const externalGroups = useMemo(
    () => permissionGroups.filter((g) => g.is_suitable_for_external_assignment),
    [permissionGroups]
  )
  const sections = useMemo(() => groupsByEntity(externalGroups), [externalGroups])

  /**
   * Toggle one or more permissions on an external role. Accepts a single
   * codename (row click) or a list (the editor's "Grant all" / "Revoke all").
   * Backend validates external-safety on every write.
   */
  async function toggleCodenameOnRole(
    role: ExternalRoleRow,
    codenameOrList: string | string[],
  ) {
    if (!orgUuid) return
    const list = Array.isArray(codenameOrList) ? codenameOrList : [codenameOrList]
    const current = new Set(role.permission_codenames ?? [])
    for (const codename of list) {
      if (current.has(codename)) current.delete(codename)
      else current.add(codename)
    }

    // Acknowledge every granted not-recommended (non-floor) codename so the
    // backend accepts the deliberate override. Floor codenames are never sent
    // as acknowledged - the backend rejects them regardless. Recommended
    // (external-safe) grants need no acknowledgement.
    const unsafeByCodename = new Map(
      allPermissions.map((p) => [p.codename, !p.is_suitable_for_external_assignment]),
    )
    const overrideUnsafe = Array.from(current).filter(
      (c) => unsafeByCodename.get(c) && !EXTERNAL_ROLE_FLOOR_CODENAMES.has(c),
    )

    setSavingRoleUuid(role.uuid)
    try {
      await api.put(`/organisations/${orgUuid}/external-roles/${role.uuid}/`, {
        name: role.name,
        description: role.description,
        permission_codenames: Array.from(current),
        override_unsafe_codenames: overrideUnsafe,
      })
      setRoles((prev) =>
        prev.map((r) =>
          r.uuid === role.uuid
            ? { ...r, permission_codenames: Array.from(current) }
            : r
        )
      )
    } catch (err: unknown) {
      toastApiError(err, 'Failed to update role.')
      fetchData()
    } finally {
      setSavingRoleUuid(null)
    }
  }

  async function applyTemplateToRole(role: ExternalRoleRow, group: PermissionGroup) {
    if (!orgUuid) return
    setSavingRoleUuid(role.uuid)
    try {
      const res = await api.put(
        `/organisations/${orgUuid}/external-roles/${role.uuid}/`,
        {
          name: role.name,
          description: role.description,
          apply_group_ids: [group.id],
        }
      )
      toast.success(`Applied "${group.name}"`)
      const updated = res.data
      setRoles((prev) =>
        prev.map((r) =>
          r.uuid === role.uuid
            ? {
                ...r,
                permission_codenames:
                  updated.permission_codenames ?? r.permission_codenames,
              }
            : r
        )
      )
    } catch (err) {
      toastApiError(err, 'Failed to apply template.');
    } finally {
      setSavingRoleUuid(null)
    }
  }

  function openCreate() {
    selectTemplate(null)
    createRoleFormRef.current?.resetForm({ values: { name: '', description: '' } })
    setDialogOpen(true)
  }

  const handleEditSave = useCallback(
    async (values: RoleEditValues) => {
      if (!orgUuid || !editRole) return
      setSavingEdit(true)
      try {
        // Partial PUT - name + description only; permissions are left untouched.
        const res = await api.put(
          `/organisations/${orgUuid}/external-roles/${editRole.uuid}/`,
          { name: values.name, description: values.description },
        )
        const updated = res.data
        setRoles((prev) =>
          prev.map((r) =>
            r.uuid === editRole.uuid
              ? { ...r, name: updated.name, description: updated.description }
              : r,
          ),
        )
        toast.success(t('roles.roleUpdated'))
        setEditRole(null)
      } catch (err) {
        toastApiError(err, t('roles.errorUpdateRole'))
      } finally {
        setSavingEdit(false)
      }
    },
    [editRole, orgUuid, t],
  )

  async function handleDelete(uuid: string) {
    setDeletingUuid(uuid)
    try {
      await api.delete(`/organisations/${orgUuid}/external-roles/${uuid}/`)
      toast.success('External role deleted.')
      setConfirmRole(null)
      fetchData()
    } catch (err) {
      toastApiError(err, 'Failed to delete role.');
    } finally {
      setDeletingUuid(null)
    }
  }

  if (!orgUuid) {
    return (
      <p className="clara-small py-8 text-center">No organisation selected.</p>
    )
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <h3 className="clara-section-label">External Roles</h3>
          <PageInfo>
            External roles are assigned to partner organisations via partnerships.
            Permissions recommended for partners are granted freely; a
            not-recommended one can be granted deliberately, per permission, with
            a confirmation. A small set of account, role, and API-key permissions
            can never be granted to a partner.
          </PageInfo>
        </div>
        <Button
          data-testid="external-roles-add-btn"
          onClick={openCreate}
          className="px-3 py-1.5 text-[11px] uppercase tracking-[0.1em] rounded-[4px] transition-all duration-150 cursor-pointer flex items-center gap-1.5 bg-[var(--green)] text-[var(--bg)] hover:bg-[var(--green-hover)]"
        >
          <Plus className="h-3 w-3" />
          Create Role
        </Button>
      </div>
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-5 w-5 animate-spin text-[var(--ink-3)]" />
        </div>
      ) : roles.length === 0 ? (
        <p className="clara-small text-center py-12">No external roles yet.</p>
      ) : (
        <div>
          <div className="grid [grid-template-columns:1fr_140px_88px_30px] gap-4 border-b-[0.5px] border-b-[var(--rule-strong)] pb-3">
            <span className="clara-th">Role</span>
            <span className="clara-th">Permissions</span>
            <span />
            <span />
          </div>
          {roles.map((role) => {
            const isExpanded = expandedUuid === role.uuid
            const grantedSet = new Set(role.permission_codenames ?? [])
            return (
              <div
                key={role.uuid}
                data-testid={`external-roles-item-${role.uuid}`}
                className="border-b-[0.5px] border-b-[var(--rule)]"
              >
                <div
                  className="grid [grid-template-columns:1fr_140px_88px_30px] items-center gap-4 py-3.5 transition-colors duration-100 cursor-pointer hover:bg-[var(--bg-subtle)]"
                  onClick={() => setExpandedUuid(isExpanded ? null : role.uuid)}
                >
                  <div>
                    <p className="text-[13px] text-[var(--ink)]">
                      {role.name}
                    </p>
                    {role.description && (
                      <p className="mt-px text-[10px] text-[var(--ink-3)]">
                        {role.description}
                      </p>
                    )}
                  </div>
                  <span className="text-[11px] text-[var(--ink-3)]">
                    {grantedSet.size} permissions
                  </span>
                  <div
                    className="flex items-center justify-end gap-0.5"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-xs"
                      data-testid={`external-roles-edit-${role.uuid}`}
                      title={t('roles.renameRole', 'Rename role')}
                      onClick={() => setEditRole(role)}
                      className="cursor-pointer"
                    >
                      <Pencil className="h-3 w-3 text-[var(--ink-3)]" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-xs"
                      data-testid={`external-roles-delete-${role.uuid}`}
                      title={t('roles.deleteRole', 'Delete role')}
                      disabled={deletingUuid === role.uuid}
                      onClick={() => setConfirmRole(role)}
                      className="cursor-pointer"
                    >
                      <Trash2 className="h-3 w-3 text-[var(--ink-3)]" />
                    </Button>
                  </div>
                  <ChevronDown
                    className={cn(
                      'h-3.5 w-3.5 text-[var(--ink-3)] transition-transform duration-150',
                      isExpanded && 'rotate-180'
                    )}
                  />
                </div>
                {isExpanded && (
                  <div className="panel-arrive pb-5 px-2">
                    <TemplateChipRow
                      sections={sections}
                      onApply={(g) => applyTemplateToRole(role, g)}
                      disabled={savingRoleUuid === role.uuid}
                      externalOnly
                    />
                    <div className="mt-4 flex items-center justify-between border-t-[0.5px] border-t-[var(--rule)] pt-4">
                      <p className="text-[11px] text-[var(--ink-3)]">
                        {t(
                          'externalRoles.fineTunePermissions',
                          'Fine-tune individual permissions for this role.',
                        )}
                      </p>
                      <Button
                        type="button"
                        variant="outline"
                        data-testid={`external-roles-edit-perms-${role.uuid}`}
                        onClick={() => setPermEditorUuid(role.uuid)}
                        className="cursor-pointer px-3 py-1.5 text-[11px] uppercase tracking-[0.1em] text-[var(--ink-2)]"
                      >
                        {t('externalRoles.editPermissions', 'Edit permissions')}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      <Dialog
        open={dialogOpen}
        onOpenChange={(open) => {
          setDialogOpen(open)
          if (!open) {
            selectTemplate(null)
            createRoleFormRef.current?.resetForm({
              values: {
                name: '',
                description: '',
              },
            })
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[15px] text-[var(--ink)]">
              {t('externalRoles.createModalTitle', 'Create external role')}
            </DialogTitle>
            <p className="text-[12px] leading-snug text-[var(--ink-3)]">
              {t(
                'externalRoles.createModalSubtitle',
                'Name the role and, optionally, start from a partner-safe template. Fine-tune permissions once it exists.',
              )}
            </p>
          </DialogHeader>

          <CreateRoleForm className="space-y-4" />
          <RoleTemplateSuggestions
            groups={permissionGroups}
            selectedId={selectedTemplateId}
            onSelect={selectTemplate}
            disabled={saving}
            externalOnly
          />

          <DialogFooter className="flex justify-end gap-3 border-t-[0.5px] border-t-[var(--rule)] pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setDialogOpen(false)}
              className="cursor-pointer px-4 py-2 text-[11px] uppercase tracking-[0.1em] text-[var(--ink-3)]"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              form="create-external-role-form"
              disabled={saving}
              data-testid="external-roles-dialog-submit"
              className="cursor-pointer rounded-[4px] bg-[var(--green)] px-4 py-2 text-[11px] uppercase tracking-[0.1em] text-[var(--bg)] transition-all duration-150 disabled:opacity-50"
            >
              {saving && <Loader2 className="h-3 w-3 animate-spin mr-1.5 inline" />}
              Create Role
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {(() => {
        const permEditorRole = roles.find((r) => r.uuid === permEditorUuid) ?? null
        return (
          <PermissionEditorModal
            open={permEditorRole !== null}
            onOpenChange={(open) => {
              if (!open) setPermEditorUuid(null)
            }}
            roleName={permEditorRole?.name ?? ''}
            roleDescription={permEditorRole?.description ?? ''}
            isAdmin={false}
            allPermissions={allPermissions}
            grantedCodenames={new Set(permEditorRole?.permission_codenames ?? [])}
            onToggle={(codename) =>
              permEditorRole && toggleCodenameOnRole(permEditorRole, codename)
            }
            saving={savingRoleUuid === permEditorRole?.uuid}
            externalSafeOnly
            canGrantUnsafe={canGrantUnsafe}
          />
        )
      })()}

      <RoleEditDialog
        open={editRole !== null}
        onOpenChange={(open) => {
          if (!open) setEditRole(null)
        }}
        title={t('roles.editExternalRole', 'Edit external role')}
        initialName={editRole?.name ?? ''}
        initialDescription={editRole?.description ?? ''}
        saving={savingEdit}
        onSave={handleEditSave}
      />

      <ConfirmDeleteDialog
        open={confirmRole !== null}
        onOpenChange={(open) => {
          if (!open) setConfirmRole(null)
        }}
        title="Delete external role?"
        description={
          confirmRole
            ? `"${confirmRole.name}" will be permanently deleted. Partners assigned this role will lose its permissions.`
            : undefined
        }
        isPending={deletingUuid === confirmRole?.uuid}
        onConfirm={() => {
          if (confirmRole) void handleDelete(confirmRole.uuid)
        }}
      />
    </div>
  );
}
